import { AxiosError } from 'axios'
import { action, computed, flow, makeAutoObservable, observable } from 'mobx'

import { requestWrapper } from 'Shared/utils'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator } from 'Core/interfaces'
import {
	convertDateFromStringToMoment,
	convertStringDateFormat,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'

import { STORE_TREATMENT_METHOD } from 'Portals/Customer/constants'
import { Dashboard } from 'Portals/Customer/entities'
import {
	BANNER_VISIBILITY_KEY,
	BANNER_VISIBILITY_VALUE
} from 'Portals/Customer/Pages/Dashboard/common/entities/Banner/utils'
import { arrayBufferToBase64 } from 'Portals/Customer/Pages/Dashboard/common/entities/Banner/utils/utils'
import { DashboardRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'

export class DashboardStore {
	rootStore: RootStore
	@observable error: AxiosError | null = null
	@observable landfills = getDefaultStoreArrayValue<Dashboard.Landfill>()
	@observable serviceSiteLandfills = getDefaultStoreArrayValue<Dashboard.ServiceSiteLandfill>()
	@observable invoiceStatement = getDefaultStoreArrayValue<Dashboard.InvoiceStatement.Basic>()
	@observable landfillStatistics = getDefaultStoreValue<Dashboard.LandfillStatictics>()
	@observable totalSpent = getDefaultStoreValue<Dashboard.TotalSpent>()
	@observable title = getDefaultStoreArrayValue<string>()
	@observable orderSummary = getDefaultStoreArrayValue<Dashboard.OrderSummary>()
	@observable waitingForResponse = getDefaultStoreArrayValue<Dashboard.WaitingForResponse>()
	@observable profileServiceSites = getDefaultStoreArrayValue<Dashboard.ProfileServiceSite>()
	@observable banners = getDefaultStoreArrayValue<Dashboard.Banner.Extended>()

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore
		makeAutoObservable(this, {}, { autoBind: true })
	}

	@computed
	get shouldShowBanners() {
		return (
			this.banners.value.length > 0 &&
			sessionStorage.getItem(BANNER_VISIBILITY_KEY) !== BANNER_VISIBILITY_VALUE
		)
	}

	@action
	hideBanners() {
		this.clearBanners()
		sessionStorage.setItem(BANNER_VISIBILITY_KEY, BANNER_VISIBILITY_VALUE)
	}

	@flow
	*retrieveBanners(): Generator<Promise<Dashboard.Banner.Basic[] | ArrayBuffer | string>> {
		this.error = null
		this.banners.isLoading = true

		try {
			const banners = yield DashboardRepo.retrieveBanners()

			this.banners.value = []

			for (const banner of banners) {
				const arrayBuffer = yield DashboardRepo.retrieveBanner(banner.id)

				const base64Image = yield arrayBufferToBase64(arrayBuffer)

				this.banners.value = [
					...this.banners.value,
					{
						...banner,
						image: base64Image
					}
				] as Dashboard.Banner.Extended[]
			}
		} catch (error) {
			this.error = error as AxiosError
		} finally {
			this.banners.isLoading = false
		}
	}

	@flow
	*retrieveDashboardTitle(): CustomGenerator<string[]> {
		yield requestWrapper({
			field: this.title,
			repository: DashboardRepo.retrieveDashboardTitle,
			repositoryParams: null
		})
	}

	@flow
	*retrieveDataWithNoPermissionsRequired() {
		this.error = null
		try {
			yield Promise.all([
				this.rootStore[STORE_TREATMENT_METHOD].retrieveTreatmentMethodsMostUsed(),
				this.retrieveLandfills(),
				this.retrieveServiceSiteLandfills(),
				this.retrieveLandfillStatistics()
			])
		} catch (error) {
			this.error = error as AxiosError
		}
	}

	@flow
	*retrieveLandfills(): CustomGenerator<Dashboard.Landfill[]> {
		yield requestWrapper({
			field: this.landfills,
			repository: DashboardRepo.retrieveLandfills,
			repositoryParams: null
		})
	}

	@flow
	*retrieveProfileServiceSite(): CustomGenerator<Dashboard.ProfileServiceSite[]> {
		yield requestWrapper({
			field: this.profileServiceSites,
			repository: DashboardRepo.retrieveProfileServiceSite,
			repositoryParams: null
		})
	}

	@flow
	*retrieveInvoiceStatement(): CustomGenerator<Dashboard.InvoiceStatement.Basic[]> {
		yield requestWrapper({
			field: this.invoiceStatement,
			repository: DashboardRepo.retrieveInvoiceStatement,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow
	*retrieveLandfillStatistics(): CustomGenerator<Dashboard.LandfillStatictics> {
		yield requestWrapper({
			field: this.landfillStatistics,
			repository: DashboardRepo.retrieveLandfillStatistics,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow
	*retrieveTotalSpent(): CustomGenerator<Dashboard.TotalSpent> {
		yield requestWrapper({
			field: this.totalSpent,
			repository: DashboardRepo.retrieveTotalSpent,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@flow
	*retrieveServiceSiteLandfills(): CustomGenerator<Dashboard.ServiceSiteLandfill[]> {
		yield requestWrapper({
			field: this.serviceSiteLandfills,
			repository: DashboardRepo.retrieveServiceSiteLandfills,
			repositoryParams: null,
			showDefaultError: false
		})
	}

	@computed
	get formattedLandfillStatistics() {
		const { value } = this.landfillStatistics
		return {
			nonLandfill: value?.nonLandfill || 0,
			landfill: value?.landfill || 0,
			nonLandfillPercent: value?.nonLandfillPercent || 0,
			landfillPercent: value?.landfillPercent || 0,
			totalSpent: this.totalSpent.value?.totalSpent || 0,
			totalSpentCurrencyCode: this.totalSpent.value?.currencyCode || '',
			total: value?.total || 0,
			totalEarning: null
		}
	}

	@computed
	get formattedServiceSiteLandfills() {
		const MAX_SERVICE_SITE_LANDFILLS = 4
		return this.serviceSiteLandfills.value.slice(0, MAX_SERVICE_SITE_LANDFILLS)
	}

	@computed
	get formattedLandfills() {
		return this.landfills.value.map((item) => ({
			label: convertDateFromStringToMoment(item.date)?.format('MMM YYYY') || '',
			value1: item.landfillShare,
			value2: item.nonLandfillShare
		}))
	}

	@computed
	get formattedOrderSummary() {
		return this.orderSummary.value.map((order) => ({
			value: order.value,
			descriptor: convertStringDateFormat(
				order.descriptor,
				this.rootStore[STORE_UI].getCurrentDateFormat
			)
		}))
	}

	@flow
	*retrieveOrderSummary(): CustomGenerator<Dashboard.OrderSummary[]> {
		yield requestWrapper({
			field: this.orderSummary,
			repository: DashboardRepo.retrieveOrderSummary,
			repositoryParams: null
		})
	}

	@flow
	*retrieveOrderWaitingForResponse(): CustomGenerator<Dashboard.WaitingForResponse[]> {
		yield requestWrapper({
			field: this.waitingForResponse,
			repository: DashboardRepo.retrieveOrderWaitingForResponse,
			repositoryParams: null
		})
	}

	@action
	clearBanners() {
		this.banners = getDefaultStoreArrayValue<Dashboard.Banner.Extended>()
	}
}
